import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { z } from 'zod'
import { randomUUID } from 'node:crypto'
import { APP_ROUTES, toAppUrl } from '@shared/app-routes'
import { SUBSCRIPTION_CANCELLATION_REASONS } from '@shared/billing'
import { enqueueServerAnalyticsEvent } from '@server/analytics'
import type Stripe from 'stripe'

const loadAuth = async () => (await import('@server/auth')).auth
const loadEnv = async () => (await import('@server/env')).env
const loadStripeClient = async () =>
  (await import('@server/stripe')).getStripeClient
const loadDbModules = async () => {
  const [{ eq }, { db }, schema] = await Promise.all([
    import('drizzle-orm'),
    import('@server/db/index'),
    import('@server/db/schema'),
  ])
  return { eq, db, schema }
}

/**
 * Input schema for creating a payment intent.
 */
const createPaymentIntentSchema = z.object({
  planId: z.enum(['premiumUser', 'proUser'], {
    error: 'Invalid plan selected. Please choose Premium or Pro.',
  }),
  checkoutAttemptId: z.string().uuid().optional(),
  analytics: z
    .object({
      visitorId: z.string().min(8).max(128),
      sessionId: z.string().min(8).max(128),
      googleClientId: z.string().min(3).max(128).nullable(),
      googleSessionId: z.string().regex(/^\d+$/).max(32).nullable(),
      googleEngagementTimeMsec: z.number().int().min(1).max(86_400_000),
      amplitudeSessionId: z.number().int().nonnegative().safe().nullable(),
    })
    .optional(),
})

const cancelSubscriptionSchema = z.object({
  reason: z.enum(SUBSCRIPTION_CANCELLATION_REASONS),
  note: z.string().trim().max(500).nullable().optional(),
})

async function getPlanPricePenceFromDb(planId: string): Promise<number> {
  const { eq, db, schema } = await loadDbModules()
  const plan = await db.query.plans.findFirst({
    where: eq(schema.plans.name, planId),
  })

  if (!plan) {
    throw new Error(
      `Missing plan configuration in database for '${planId}'. Run npm run db:seed:plans (local) or npm run db:seed:plans:remote (remote).`,
    )
  }

  if (
    !Number.isInteger(plan.pricePenceMonthly) ||
    plan.pricePenceMonthly <= 0
  ) {
    throw new Error(
      `Invalid monthly price configuration in database for '${planId}'. Run npm run db:seed:plans (local) or npm run db:seed:plans:remote (remote).`,
    )
  }

  return plan.pricePenceMonthly
}

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing', 'past_due'])
type SubscriptionRecord = {
  id: string
  userId: string
  stripeCustomerId: string | null
  stripeSubscriptionId: string | null
  stripePriceId: string | null
  status: string
  currentPeriodEnd: Date | null
  cancelAtPeriodEnd: boolean
  nextBillingAmount: number | null
  renewalFailureCount: number | null
  lastPaymentAttempt: Date | null
  updatedAt: Date | number | null
}

function toEpochMs(value: Date | number | null | undefined) {
  if (value instanceof Date) return value.getTime()
  if (typeof value === 'number') return value
  return 0
}

async function getCurrentSubscriptionForUser(userId: string) {
  const { db } = await loadDbModules()
  const subscriptions = await db.query.subscription.findMany({
    where: (subscription, { eq: equals }) =>
      equals(subscription.userId, userId),
  })

  if (subscriptions.length === 0) return null

  const sortedByRecentUpdate = [...subscriptions].sort(
    (a, b) => toEpochMs(b.updatedAt) - toEpochMs(a.updatedAt),
  )

  const activeSubscription = sortedByRecentUpdate.find((subscription) =>
    ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.status),
  )
  if (activeSubscription) return activeSubscription

  const incompleteWithPeriod = sortedByRecentUpdate.find(
    (subscription) =>
      subscription.status === 'incomplete' && subscription.currentPeriodEnd,
  )
  if (incompleteWithPeriod) return incompleteWithPeriod

  return sortedByRecentUpdate[0]
}

function resolvePaidPlan(plan: string | null | undefined) {
  if (plan === 'premiumUser' || plan === 'proUser') return plan
  return null
}

function resolveActivePaidPlanFromSubscription(
  subscription: SubscriptionRecord | null,
) {
  if (!subscription) return null
  if (!ACTIVE_SUBSCRIPTION_STATUSES.has(subscription.status)) return null
  return resolvePaidPlan(subscription.stripePriceId)
}

/**
 * Creates a Stripe PaymentIntent for the current user to upgrade to a paid plan.
 */
export const createPaymentIntent = createServerFn({ method: 'POST' })
  .inputValidator(createPaymentIntentSchema)
  .handler(async ({ data }) => {
    const request = getRequest()
    const [env, auth, getStripeClient, { eq, db, schema }] = await Promise.all([
      loadEnv(),
      loadAuth(),
      loadStripeClient(),
      loadDbModules(),
    ])
    if (!env.STRIPE_SECRET_KEY) {
      throw new Error('Stripe configuration missing (STRIPE_SECRET_KEY)')
    }

    const session = await auth.api.getSession({ headers: request.headers })
    if (!session?.user) {
      throw new Error('Unauthorized. Please log in to subscribe.')
    }

    // Check if user already has an active subscription
    const existingSubscription = await getCurrentSubscriptionForUser(
      session.user.id,
    )

    const currentPaidPlan =
      resolveActivePaidPlanFromSubscription(existingSubscription) ??
      (existingSubscription ? null : resolvePaidPlan(session.user.billingPlan))

    if (currentPaidPlan === data.planId) {
      throw new Error('You are already on this plan.')
    }

    // Resolve selected plan amount from database (single source of truth).
    const amount = await getPlanPricePenceFromDb(data.planId)
    const checkoutAttemptId = data.checkoutAttemptId ?? randomUUID()

    // Get the Stripe client
    const stripe = getStripeClient()

    // Step 1: Get or create a Stripe Customer for the user
    let stripeCustomerId: string

    if (existingSubscription?.stripeCustomerId) {
      // Reuse the existing customer ID
      stripeCustomerId = existingSubscription.stripeCustomerId
    } else {
      // Create a new Stripe Customer
      const customer = await stripe.customers.create({
        email: session.user.email,
        name: session.user.name,
        metadata: {
          userId: session.user.id,
        },
      })
      stripeCustomerId = customer.id
    }

    // Step 2: Create the Stripe PaymentIntent with setup_future_usage to save payment method
    let paymentIntent: Stripe.PaymentIntent
    try {
      paymentIntent = await stripe.paymentIntents.create({
        amount,
        currency: 'gbp',
        customer: stripeCustomerId,
        setup_future_usage: 'off_session', // Save payment method for automatic renewals
        automatic_payment_methods: {
          enabled: true,
        },
        metadata: {
          userId: session.user.id,
          planId: data.planId,
          previousPlanId: currentPaidPlan ?? 'registeredUser',
          checkoutAttemptId,
          ...(data.analytics
            ? {
                visitorId: data.analytics.visitorId,
                productSessionId: data.analytics.sessionId,
                ...(data.analytics.googleClientId
                  ? { googleClientId: data.analytics.googleClientId }
                  : {}),
                ...(data.analytics.googleSessionId
                  ? { googleSessionId: data.analytics.googleSessionId }
                  : {}),
                googleEngagementTimeMsec: String(
                  data.analytics.googleEngagementTimeMsec,
                ),
                ...(data.analytics.amplitudeSessionId === null
                  ? {}
                  : {
                      amplitudeSessionId: String(
                        data.analytics.amplitudeSessionId,
                      ),
                    }),
              }
            : {}),
        },
      })
    } catch (stripeError: unknown) {
      if (stripeError instanceof Error) {
        throw new Error(
          `Failed to create payment intent: ${stripeError.message}`,
        )
      }
      throw new Error('Failed to create payment intent. Please try again.')
    }

    // Step 3: Persist the pending subscription and its authoritative analytics
    // event atomically. The Stripe intent may exist independently, but the
    // application only reports checkout preparation after its own state is
    // durable.
    await db.transaction(async (tx) => {
      if (!existingSubscription) {
        await tx.insert(schema.subscription).values({
          userId: session.user.id,
          stripeCustomerId,
          stripeSubscriptionId: paymentIntent.id, // Using PaymentIntent ID as subscription ID
          stripePriceId: data.planId, // Store plan ID instead of Stripe price ID
          status: 'incomplete',
          currentPeriodEnd: null, // Will be set on successful payment
          cancelAtPeriodEnd: false,
          nextBillingAmount: amount, // Store amount for next billing cycle
        })
      } else {
        // Update existing subscription with new payment intent
        await tx
          .update(schema.subscription)
          .set({
            stripeSubscriptionId: paymentIntent.id,
            stripePriceId: data.planId,
            status: 'incomplete',
            currentPeriodEnd: null,
            nextBillingAmount: amount,
          })
          .where(eq(schema.subscription.id, existingSubscription.id))
      }

      await enqueueServerAnalyticsEvent(
        {
          eventName: 'checkout_prepared',
          idempotencyKey: `checkout:${paymentIntent.id}:prepared`,
          userId: session.user.id,
          visitorId: data.analytics?.visitorId,
          sessionId: data.analytics?.sessionId,
          googleClientId: data.analytics?.googleClientId,
          googleSessionId: data.analytics?.googleSessionId,
          googleEngagementTimeMsec: data.analytics?.googleEngagementTimeMsec,
          amplitudeSessionId: data.analytics?.amplitudeSessionId,
          properties: {
            checkout_id: paymentIntent.id,
            checkout_attempt_id: checkoutAttemptId,
            plan_id: data.planId,
            previous_plan_id: currentPaidPlan,
            amount_minor: amount,
            currency: 'GBP',
          },
        },
        tx,
      )
    })

    // Step 4: Return the clientSecret for the frontend
    return {
      clientSecret: paymentIntent.client_secret as string,
      paymentIntentId: paymentIntent.id,
      stripeCustomerId,
      amount: amount / 100, // Convert back to pounds for display
      currency: 'GBP',
    }
  })

export const getStripePublishableKey = createServerFn({
  method: 'GET',
}).handler(async () => {
  const env = await loadEnv()
  return {
    publishableKey:
      env.STRIPE_PUBLISHABLE_KEY ?? env.VITE_STRIPE_PUBLISHABLE_KEY ?? '',
  }
})

/**
 * Gets the current subscription status for the authenticated user.
 */
export const getSubscriptionStatus = createServerFn({ method: 'GET' }).handler(
  async () => {
    const request = getRequest()
    const auth = await loadAuth()
    const session = await auth.api.getSession({ headers: request.headers })
    if (!session?.user) {
      throw new Error('Unauthorized')
    }

    const subscription = await getCurrentSubscriptionForUser(session.user.id)

    if (!subscription) {
      return { hasSubscription: false }
    }

    return {
      hasSubscription: true,
      status: subscription.status,
      currentPeriodEnd: subscription.currentPeriodEnd?.toISOString() ?? null,
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
      planId: subscription.stripePriceId, // This is our planId (premiumUser, proUser)
    }
  },
)

/**
 * Gets detailed subscription information for the authenticated user.
 */
export const getSubscriptionDetails = createServerFn({ method: 'GET' }).handler(
  async () => {
    const request = getRequest()
    const auth = await loadAuth()
    const session = await auth.api.getSession({ headers: request.headers })
    if (!session?.user) {
      throw new Error('Unauthorized')
    }

    const subscription = await getCurrentSubscriptionForUser(session.user.id)

    if (!subscription) {
      return { hasSubscription: false }
    }

    return {
      hasSubscription: true,
      id: subscription.id,
      status: subscription.status,
      currentPeriodEnd: subscription.currentPeriodEnd?.toISOString() ?? null,
      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
      planId: subscription.stripePriceId,
      nextBillingAmount: subscription.nextBillingAmount,
      renewalFailureCount: subscription.renewalFailureCount,
      lastPaymentAttempt:
        subscription.lastPaymentAttempt?.toISOString() ?? null,
    }
  },
)

/**
 * Cancels a subscription at the end of the current billing period.
 */
export const cancelSubscriptionAtPeriodEnd = createServerFn({
  method: 'POST',
})
  .inputValidator(cancelSubscriptionSchema)
  .handler(async ({ data }) => {
    const request = getRequest()
    const [auth, { eq, db, schema }] = await Promise.all([
      loadAuth(),
      loadDbModules(),
    ])
    const session = await auth.api.getSession({ headers: request.headers })
    if (!session?.user) {
      throw new Error('Unauthorized')
    }

    const subscription = await getCurrentSubscriptionForUser(session.user.id)

    if (!subscription) {
      throw new Error('No subscription found')
    }

    if (subscription.cancelAtPeriodEnd) {
      throw new Error('Subscription is already set to cancel')
    }

    const feedbackId = randomUUID()
    const note = data.note?.trim() || null
    await db.transaction(async (tx) => {
      await tx.insert(schema.subscriptionCancellationFeedback).values({
        id: feedbackId,
        subscriptionId: subscription.id,
        userId: session.user.id,
        reason: data.reason,
        note,
      })

      await tx
        .update(schema.subscription)
        .set({ cancelAtPeriodEnd: true })
        .where(eq(schema.subscription.id, subscription.id))

      await enqueueServerAnalyticsEvent(
        {
          eventName: 'subscription_cancellation_scheduled',
          idempotencyKey: `subscription:${subscription.id}:cancellation:${feedbackId}`,
          userId: session.user.id,
          properties: {
            subscription_id: subscription.id,
            plan_id: subscription.stripePriceId,
            reason: data.reason,
            current_period_end:
              subscription.currentPeriodEnd?.toISOString() ?? null,
          },
        },
        tx,
      )
    })

    return { success: true }
  })

/**
 * Reactivates a subscription that was set to cancel.
 */
export const reactivateSubscription = createServerFn({
  method: 'POST',
}).handler(async () => {
  const request = getRequest()
  const [auth, { eq, db, schema }] = await Promise.all([
    loadAuth(),
    loadDbModules(),
  ])
  const session = await auth.api.getSession({ headers: request.headers })
  if (!session?.user) {
    throw new Error('Unauthorized')
  }

  const subscription = await getCurrentSubscriptionForUser(session.user.id)

  if (!subscription) {
    throw new Error('No subscription found')
  }

  if (!subscription.cancelAtPeriodEnd) {
    throw new Error('Subscription is not set to cancel')
  }

  const reactivatedAt = new Date()
  await db.transaction(async (tx) => {
    await tx
      .update(schema.subscription)
      .set({ cancelAtPeriodEnd: false })
      .where(eq(schema.subscription.id, subscription.id))

    await enqueueServerAnalyticsEvent(
      {
        eventName: 'subscription_reactivated',
        idempotencyKey: `subscription:${subscription.id}:reactivated:${reactivatedAt.toISOString()}`,
        userId: session.user.id,
        properties: {
          subscription_id: subscription.id,
          plan_id: subscription.stripePriceId,
          current_period_end:
            subscription.currentPeriodEnd?.toISOString() ?? null,
        },
      },
      tx,
    )
  })

  return { success: true }
})

/**
 * Creates a SetupIntent for updating the payment method.
 * Returns a clientSecret for the frontend to use with Stripe Elements.
 */
export const createSetupIntent = createServerFn({ method: 'POST' }).handler(
  async () => {
    const request = getRequest()
    const [env, auth, getStripeClient] = await Promise.all([
      loadEnv(),
      loadAuth(),
      loadStripeClient(),
    ])
    if (!env.STRIPE_SECRET_KEY) {
      throw new Error('Stripe configuration missing')
    }

    const session = await auth.api.getSession({ headers: request.headers })
    if (!session?.user) {
      throw new Error('Unauthorized')
    }

    const stripe = getStripeClient()

    // Get the user's Stripe customer ID
    const subscription = await getCurrentSubscriptionForUser(session.user.id)

    if (!subscription?.stripeCustomerId) {
      throw new Error('No billing account found. Please subscribe first.')
    }

    // Create a SetupIntent to attach a new payment method
    const setupIntent = await stripe.setupIntents.create({
      customer: subscription.stripeCustomerId,
      payment_method_types: ['card'],
      usage: 'off_session',
    })

    return {
      clientSecret: setupIntent.client_secret as string,
    }
  },
)

/**
 * Creates a Stripe Billing Portal session for managing subscriptions.
 * This allows users to update payment methods, view invoices, and cancel.
 */
export const createBillingPortalSession = createServerFn({
  method: 'POST',
}).handler(async () => {
  const request = getRequest()
  const [env, auth, getStripeClient] = await Promise.all([
    loadEnv(),
    loadAuth(),
    loadStripeClient(),
  ])
  if (!env.STRIPE_SECRET_KEY) {
    throw new Error('Stripe configuration missing')
  }

  const session = await auth.api.getSession({ headers: request.headers })
  if (!session?.user) {
    throw new Error('Unauthorized')
  }

  const stripe = getStripeClient()

  // Get the user's Stripe customer ID
  const subscription = await getCurrentSubscriptionForUser(session.user.id)

  if (!subscription?.stripeCustomerId) {
    throw new Error('No billing account found. Please subscribe first.')
  }

  // Create the portal session
  const portalSession = await stripe.billingPortal.sessions.create({
    customer: subscription.stripeCustomerId,
    return_url: toAppUrl(
      APP_ROUTES.dashboard.subscription,
      env.BETTER_AUTH_URL,
    ),
  })

  return { url: portalSession.url }
})
